import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { WalletProvider, useWallet } from './context/WalletProvider';
import { ToastProvider } from './components/Toast';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import Loader from './components/Loader';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Team from './pages/Team';
import Reports from './pages/Reports';
import Account from './pages/Account';
import Admin from './pages/Admin';
import { fetchRoles, fetchUser } from './services/zen';
import { captureRef } from './utils/referral';

// capture ?ref= as early as possible (survives the connect round-trip)
captureRef();

function Shell() {
  const { provider, address, isConnected, isCorrectNetwork } = useWallet();
  const location = useLocation();
  const navigate = useNavigate();

  const [roles, setRoles] = useState(null);
  const [registered, setRegistered] = useState(null); // null = unknown yet
  const [bootLoading, setBootLoading] = useState(false);

  // on connect: read roles + whether this wallet is registered
  useEffect(() => {
    let alive = true;
    if (!provider || !address || !isCorrectNetwork) {
      setRoles(null);
      setRegistered(null);
      setBootLoading(false);
      return;
    }
    (async () => {
      try {
        setBootLoading(true);
        const [r, u] = await Promise.all([
          fetchRoles(provider).catch(() => null),
          fetchUser(provider, address).catch(() => null),
        ]);
        if (!alive) return;
        setRoles(r);
        setRegistered(u ? !!u.registered : false);
      } finally {
        if (alive) setBootLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [provider, address, isCorrectNetwork]);

  // auto-route an unregistered wallet to the register screen (once known)
  useEffect(() => {
    if (registered === false && location.pathname !== '/account') {
      navigate('/account', { replace: true });
    }
  }, [registered, location.pathname, navigate]);

  const isAdmin =
    roles && address &&
    (address.toLowerCase() === roles.owner || address.toLowerCase() === roles.admin);

  // ---- disconnected: full landing page ----
  if (!isConnected) {
    return (
      <div className="shell">
        <Header />
        <Landing />
      </div>
    );
  }

  // ---- connected ----
  return (
    <>
      {bootLoading && <Loader messages={['Reading the chain', 'Syncing your account']} />}
      <div className="shell">
        <Header />
        {!isCorrectNetwork && (
          <div className="banner bad">You're on the wrong network — tap “Fix” above to switch automatically.</div>
        )}
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/team" element={<Team />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/account" element={<Account roles={roles} onRegistered={() => { setRegistered(true); navigate('/', { replace: true }); }} />} />
          <Route path="/admin" element={isAdmin ? <Admin roles={roles} /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {/* hide bottom nav until we know they're registered, to keep the
          register screen clean for brand-new users */}
      {registered !== false && <BottomNav showAdmin={isAdmin} />}
    </>
  );
}

export default function App() {
  return (
    <WalletProvider>
      <ToastProvider>
        <Shell />
      </ToastProvider>
    </WalletProvider>
  );
}
